import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge, BmModal, Btn, Card, ConfirmModal, EmptyState, Overline, Select } from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';
import CascadePicker from '../../ForBusiness/Cacheshare/CascadePicker';
import { resolveFetchPlan, legacySportKey, GOVERNING_BODIES } from '../../ForBusiness/Cacheshare/taxonomy';
import { executeFetchPlan } from '../../ForBusiness/Cacheshare/services/cacheShareServices';
import {
  adminAddCoachTeam,
  adminRemoveCoachTeam,
  adminGenerateCoachCode,
  adminSetLeagueAdmin,
  adminClearLeagueAdmin,
} from '../../auth/Onboarding/services/onboardingServices';
import { EMAIL_UNPROVEN_HINT, EMAIL_UNPROVEN_LABEL, refusalMessage } from './coachProof';
import { displayTeamName, stripSeasonSuffix } from '../../../utils/teamNames';
import { pickedTeamBinding } from '../../ForCoaches/v2/onboarding/coachOnboardingModel';

// Flatten the HS/grassroots governing bodies into pickable "leagues" — each
// option grants the WHOLE family (every gender + age + sub-division's Cerebro
// keys), so "Nike EYBL" hands the account all of EYBL.
const LEAGUE_OPTIONS = (() => {
  const out = [];
  const seen = new Set();
  const hs = GOVERNING_BODIES?.hs || {};
  for (const region of Object.values(hs)) {
    for (const body of (region?.list || [])) {
      if (!body || seen.has(body.value)) continue;
      const sports = new Set();
      for (const g of ['M', 'F']) {
        const branch = body.byGender?.[g] || {};
        for (const s of (branch.cerebroSports || [])) sports.add(s);
        for (const sub of (branch.subDivisions || [])) {
          for (const s of (sub.cerebroSports || [])) sports.add(s);
          for (const s of (sub.byGender?.[g]?.cerebroSports || [])) sports.add(s);
        }
      }
      for (const s of (body.cerebroSports || [])) sports.add(s);
      if (sports.size) {
        seen.add(body.value);
        out.push({ value: body.value, label: body.name, cerebroSports: Array.from(sports) });
      }
    }
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
})();

const EMPTY_FILTERS = {
  sport: null,
  gender: null,
  tier: null,
  region: null,
  body: null,
  subDivision: null,
  classDivision: null,
  conference: null,
  teamSearch: '',
};

// Module-level frozen empty object so `coach?.coachVerification || EMPTY_CV`
// returns a stable reference on every render when the dialog has no coach
// loaded. Inline `|| {}` made `cv` a fresh object each render → the
// `propList` useMemo recomputed → the `setTeamsLocal` useEffect saw a new
// dep → set state → re-render → infinite loop.
const EMPTY_CV = Object.freeze({});

// Admin dialog that replaces the old "Permissions" panel for coach rows.
// Shows the coach's primary + additional teams, with an "Add team"
// cascade picker (same UX as coach onboarding) and per-row remove.
const CoachTeamsDialog = ({ open, coach, onClose, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // The team being removed: its id and league (the squads of a program
  // share the id), as `${teamId}|${league}`.
  const [removingId, setRemovingId] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState('');
  const [savingLeague, setSavingLeague] = useState(false);
  const [confirmLeague, setConfirmLeague] = useState(null); // the option pending confirmation
  const [confirmRemove, setConfirmRemove] = useState(null); // { teamId, league, name } pending removal
  const [confirmClearLeague, setConfirmClearLeague] = useState(false);

  const cv = coach?.coachVerification || EMPTY_CV;
  // League-admin grant (gives the account every team in the league across the
  // dashboard + CacheShare). Seeded from the coach prop, updated optimistically.
  const [leagueGrant, setLeagueGrant] = useState(cv.leagueAdmin || null);
  useEffect(() => { setLeagueGrant(cv.leagueAdmin || null); }, [cv.leagueAdmin]);
  // Local team list — seeded from the coach prop, then mutated optimistically
  // on add/remove using the backend's response. Without this, the panel
  // would only refresh after the parent's user-list refetch round-trip,
  // which made admins think the add didn't take.
  const propList = useMemo(() => {
    const out = [];
    if (cv.teamId) {
      out.push({
        teamId: cv.teamId,
        league: cv.league,
        currentTeam: cv.currentTeam,
        cascade: cv.cascade,
        isPrimary: true,
      });
    }
    for (const t of (cv.additionalTeams || [])) {
      out.push({ ...t, isPrimary: false });
    }
    return out;
  }, [cv]);
  const [teamsLocal, setTeamsLocal] = useState(propList);
  // Re-seed when the coach prop swaps (different row, or parent refetch).
  useEffect(() => { setTeamsLocal(propList); }, [propList]);
  const list = teamsLocal;

  // Verification code — seeded from the coach prop, updated optimistically
  // after generate so the new code shows without a parent refetch.
  const [code, setCode] = useState(cv.verificationCode || '');
  useEffect(() => { setCode(cv.verificationCode || ''); }, [cv.verificationCode]);

  const fetchPlan = useMemo(() => resolveFetchPlan({
    sportValue: filters.sport?.value,
    gender: filters.gender?.value,
    tier: filters.tier?.value,
    region: filters.region?.value,
    body: filters.body,
    subDivision: filters.subDivision,
    classDivision: filters.classDivision,
    conference: filters.conference?.value,
  }), [filters]);

  // Cascade-driven team fetch.
  useEffect(() => {
    if (!adding || !fetchPlan) {
      setTeams([]);
      return;
    }
    let cancelled = false;
    setTeamsLoading(true);
    // Every team of the league, also football teams without saved stats
    // while the provider is down: this picker binds a team, it scores none.
    executeFetchPlan(fetchPlan, { allTeams: true })
      .then((rows) => {
        if (cancelled) return;
        // Hide teams the coach already has (primary or additional) so an
        // admin can't accidentally pick a duplicate. The backend rejects
        // duplicates too as a safety net. A squad is its id and league: a
        // row of a held program under its own squad key (another age
        // group) stays pickable; one with no key of its own, or held under
        // that key or with no league, is hidden.
        const held = new Set(list.map((t) => `${t.teamId || ''}|${t.league || ''}`));
        const heldIds = new Set(list.map((t) => t.teamId).filter(Boolean));
        const isHeld = (r) => {
          if (!heldIds.has(r.id)) return false;
          const own = r.catalogueKey || r.cacheLeague || '';
          return !own || held.has(`${r.id}|${own}`) || held.has(`${r.id}|`);
        };
        setTeams((rows || []).filter((r) => !isHeld(r)));
      })
      .catch(() => { if (!cancelled) setTeams([]); })
      .finally(() => { if (!cancelled) setTeamsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adding, JSON.stringify(fetchPlan), list.length]);

  // Reset add-flow state whenever the dialog opens / closes.
  useEffect(() => {
    if (!open) {
      setAdding(false);
      setFilters(EMPTY_FILTERS);
      setSelectedTeam(null);
      setConfirmRotate(false);
      setConfirmLeague(null);
      setConfirmRemove(null);
      setConfirmClearLeague(false);
    }
  }, [open]);

  const submitAdd = async () => {
    if (!selectedTeam?.id || !fetchPlan) return;
    // The picked row's own binding (pickedTeamBinding): a partner team's
    // CACHE league, a Cerebro squad's own key (plus its 'competition|
    // division', which the server files the squad by: a list tags every
    // Cerebro row with the scope's first key), a SportsRadar or Genius
    // team's league, and the row's own source (never 'multi').
    const binding = pickedTeamBinding(selectedTeam, fetchPlan);
    if (!binding.league) {
      toast.error('Could not resolve the league key.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        teamId: selectedTeam.id,
        league: binding.league,
        ...(binding.leagueKey ? { leagueKey: binding.leagueKey } : {}),
        teamSource: binding.teamSource,
        currentTeam: selectedTeam.name || selectedTeam.market || '',
        cascade: {
          sportLabel: filters.sport?.name || '',
          genderLabel: filters.gender?.name || '',
          tierLabel: filters.tier?.name || '',
          regionLabel: filters.region?.name || '',
          bodyLabel: filters.body?.name || '',
          subDivisionLabel: filters.subDivision?.name || '',
          classDivisionLabel: filters.classDivision?.name || '',
          conferenceLabel: filters.conference?.name || '',
          // Stored without the season CACHE adds to a label (D15).
          leagueDisplayName: stripSeasonSuffix(selectedTeam.leagueName || ''),
          sport: legacySportKey(filters.sport?.value, filters.gender?.value),
        },
      };
      const resp = await adminAddCoachTeam(coach.email, payload);
      // Backend returns the fresh team list — apply locally so the panel
      // reflects the new team without waiting on the parent's user-list
      // refetch round-trip. Each team comes with its code (teamCode); the
      // primary team's is the coach's code.
      if (Array.isArray(resp?.teams)) {
        setTeamsLocal(resp.teams);
        const primaryRow = resp.teams.find((t) => t.isPrimary);
        if (primaryRow?.teamCode) setCode(primaryRow.teamCode);
      }
      const coachLabel = coach.name || coach.email;
      const shown = displayTeamName({ ...selectedTeam, teamName: payload.currentTeam }) || payload.currentTeam;
      if (resp?.becamePrimary) {
        toast.success(
          `Set ${shown} as ${coachLabel}'s primary team. Onboarding is marked complete.`
        );
      } else {
        toast.success(`Added ${shown} to ${coachLabel}.`);
      }
      setAdding(false);
      setFilters(EMPTY_FILTERS);
      setSelectedTeam(null);
      onChange?.();
    } catch (err) {
      // Surface the backend's message verbatim — handles the duplicate
      // case ("This team is already on the coach."), a coach whose email is
      // unproven, and any future server-side reject reasons.
      toast.error(refusalMessage(err, 'Could not add team.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Runs once the admin confirms in the Remove team ConfirmModal. Only that
  // squad goes: its league goes with the id.
  const submitRemove = async ({ teamId, league }) => {
    setRemovingId(`${teamId}|${league || ''}`);
    try {
      const resp = league ? await adminRemoveCoachTeam(coach.email, teamId, league) : await adminRemoveCoachTeam(coach.email, teamId);
      if (Array.isArray(resp?.teams)) setTeamsLocal(resp.teams);
      toast.success('Team removed.');
      onChange?.();
    } catch (err) {
      toast.error(refusalMessage(err, 'Could not remove team.'));
    } finally {
      setRemovingId(null);
      setConfirmRemove(null);
    }
  };

  // Generating a first code is non-destructive (one click). Rotating an
  // existing code invalidates it for new athletes, so that goes through the
  // ConfirmModal below.
  const handleGenerateCode = () => {
    if (code) { setConfirmRotate(true); return; }
    doGenerateCode();
  };

  const doGenerateCode = async () => {
    setConfirmRotate(false);
    setGeneratingCode(true);
    try {
      const resp = await adminGenerateCoachCode(coach.email);
      const newCode = resp?.verificationCode || '';
      setCode(newCode);
      navigator.clipboard?.writeText(newCode).then(
        () => toast.success(`Code generated and copied: ${newCode}`),
        () => toast.success(`Code generated: ${newCode}`),
      );
      onChange?.();
    } catch (err) {
      toast.error(refusalMessage(err, 'Could not generate code.'));
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCode = () => {
    if (!code) return;
    navigator.clipboard?.writeText(code).then(
      () => toast.success(`Code copied: ${code}`),
      () => toast.error('Could not copy code'),
    );
  };

  const handleMakeLeagueAdmin = async () => {
    const opt = confirmLeague;
    if (!opt) return;
    setSavingLeague(true);
    try {
      const resp = await adminSetLeagueAdmin(coach.email, {
        label: opt.label, cerebroSports: opt.cerebroSports,
      });
      setLeagueGrant(resp?.leagueAdmin || { label: opt.label, cerebroSports: opt.cerebroSports });
      setSelectedLeague('');
      toast.success(`${coach.name || coach.email} now sees all of ${opt.label}.`);
      onChange?.();
    } catch (err) {
      toast.error(refusalMessage(err, 'Could not grant league admin.'));
    } finally {
      setSavingLeague(false);
      setConfirmLeague(null);
    }
  };

  // Runs once the admin confirms in the Remove league admin ConfirmModal.
  const handleClearLeagueAdmin = async () => {
    setSavingLeague(true);
    try {
      await adminClearLeagueAdmin(coach.email);
      setLeagueGrant(null);
      toast.success('League-admin access removed.');
      onChange?.();
    } catch (err) {
      toast.error(refusalMessage(err, 'Could not remove league admin.'));
    } finally {
      setSavingLeague(false);
      setConfirmClearLeague(false);
    }
  };


  if (!open || !coach) return null;

  const coachLabel = coach.name || coach.email;
  const cancelAdd = () => { setAdding(false); setFilters(EMPTY_FILTERS); setSelectedTeam(null); };
  const requestLeagueAdmin = () => {
    const opt = LEAGUE_OPTIONS.find((o) => o.value === selectedLeague);
    if (!opt) { toast.error('Pick a league first.'); return; }
    setConfirmLeague(opt);
  };

  return (
    <>
      {/* Each confirm opens as a second BmModal on top, so Escape and its
          backdrop close only the confirm. */}
      <BmModal
        title="Manage teams"
        onClose={onClose}
        width={760}
        footer={<Btn variant="secondary" onClick={onClose}>Close</Btn>}
      >
        <p className="bm-body-s text-bm-muted">{coachLabel} · program-director access for multiple teams</p>
        {coach.emailUnproven && (
          <div className="mt-3 flex items-start gap-2 rounded-[8px] border border-bm-attention bg-bm-subtle px-3 py-2">
            <Icon name="alert-triangle" className="mt-[2px] text-bm-ink" />
            <p className="bm-body-s text-bm-ink">{EMAIL_UNPROVEN_LABEL}. {EMAIL_UNPROVEN_HINT}</p>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-6">
          {/* Teams: the primary one first, then the additional ones. */}
          <section className="flex flex-col gap-2">
            <Overline>Teams ({list.length})</Overline>
            {list.length === 0 ? (
              <EmptyState>
                No team assigned yet. Adding one will set it as the coach's primary team and skip the onboarding wizard for them.
              </EmptyState>
            ) : list.map((t) => {
              // One code per team, shared by every coach of the team: the
              // primary team's is the coach's code below, an additional
              // team's is on its own entry.
              const teamCode = t.isPrimary ? code : (t.teamCode || t.verificationCode || '');
              const rowKey = `${t.teamId}|${t.league || ''}`;
              // The name people read: a squad's age group, no season (D15).
              const shown = t.currentTeam ? displayTeamName({ ...t, teamName: t.currentTeam }) || t.currentTeam : t.teamId;
              return (
                <Card key={rowKey} padding="px-4 py-3" className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="bm-label-l text-bm-ink">{shown}</span>
                      {t.isPrimary && (
                        <Badge tone="brand">
                          <Icon name="star" size={12} />
                          Primary
                        </Badge>
                      )}
                      {teamCode && (
                        <Badge tone="soft" className="font-mono">
                          {`Team code ${teamCode}`}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 bm-body-s text-bm-muted">
                      {[t.cascade?.tier, t.cascade?.body, t.cascade?.subDivision, t.cascade?.conference, stripSeasonSuffix(t.cascade?.leagueDisplayName || '') || t.league]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {!t.isPrimary && (
                    <Btn
                      size="sm"
                      variant="danger"
                      onClick={() => setConfirmRemove({ teamId: t.teamId, league: t.league || '', name: shown })}
                      disabled={removingId === rowKey}
                    >
                      <Icon name="trash" size={14} className="mr-1.5" />
                      {removingId === rowKey ? 'Removing...' : 'Remove'}
                    </Btn>
                  )}
                </Card>
              );
            })}
          </section>

          {/* Verification code: the primary team's code. */}
          <section className="flex flex-col gap-2">
            <Overline>Verification code</Overline>
            <p className="bm-body-s text-bm-muted">
              The code of the primary team. Every coach of a team shares its code; each additional team has its own.
            </p>
            <Card padding="px-4 py-3" className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <Icon name="key" className="text-bm-muted" />
                {code ? (
                  <button
                    type="button"
                    onClick={copyCode}
                    title="Click to copy"
                    aria-label={`Copy code ${code}`}
                    className="rounded-full hover:opacity-80"
                  >
                    <Badge tone="soft" className="font-mono">
                      {code}
                      <Icon name="copy" size={12} />
                    </Badge>
                  </button>
                ) : (
                  <span className="bm-body-s text-bm-muted">No code yet. Athletes need this to link to the coach.</span>
                )}
              </div>
              <Btn size="sm" onClick={handleGenerateCode} disabled={generatingCode}>
                <Icon name="refresh" size={14} className={`mr-1.5 ${generatingCode ? 'animate-spin' : ''}`} />
                {generatingCode ? 'Generating...' : code ? 'Generate new code' : 'Generate code'}
              </Btn>
            </Card>
          </section>

          {/* League admin: grant the account every team in a whole league
              (CacheShare and the team-players dashboard, all teams). */}
          <section className="flex flex-col gap-2">
            <Overline>League admin</Overline>
            {leagueGrant ? (
              <Card padding="px-4 py-3" className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Icon name="globe" className="text-bm-muted" />
                  <span className="bm-body-m text-bm-ink">
                    Sees every team in <span className="font-semibold">{leagueGrant.label}</span> (dashboard + CacheShare).
                  </span>
                </div>
                <Btn size="sm" variant="danger" onClick={() => setConfirmClearLeague(true)} disabled={savingLeague}>
                  <Icon name="trash" size={14} className="mr-1.5" />
                  Remove
                </Btn>
              </Card>
            ) : (
              <Card padding="px-4 py-3" className="flex flex-wrap items-center gap-3">
                <Select
                  value={selectedLeague}
                  onChange={(e) => setSelectedLeague(e.target.value)}
                  aria-label="League"
                  className="min-w-0 flex-[1_1_200px]"
                >
                  <option value="">Select a league</option>
                  {LEAGUE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
                <Btn size="sm" onClick={requestLeagueAdmin} disabled={!selectedLeague || savingLeague}>
                  <Icon name="globe" size={14} className="mr-1.5" />
                  Make league admin
                </Btn>
              </Card>
            )}
            <p className="bm-body-s text-bm-muted">
              Grants access to all teams in the whole league (every gender + age group), with CacheShare and roster views for each.
            </p>
          </section>

          {/* Add a team */}
          {!adding ? (
            <Btn onClick={() => setAdding(true)} className="w-full">
              <Icon name="plus" className="mr-1.5" />
              {list.length === 0 ? 'Add primary team' : 'Add another team'}
            </Btn>
          ) : (
            <section className="flex flex-col gap-2">
              <Overline>Add a team</Overline>
              <Card padding="p-5">
                <CascadePicker
                  filters={filters}
                  onFiltersChange={setFilters}
                  teams={teams}
                  selectedTeam={selectedTeam}
                  onTeamSelect={setSelectedTeam}
                  isLoadingTeams={teamsLoading}
                  layout="modal"
                  variant="v2"
                />
                <div className="mt-5 flex flex-wrap justify-end gap-3">
                  <Btn variant="secondary" onClick={cancelAdd}>Cancel</Btn>
                  {/* A long team name truncates instead of running past the card on a phone. */}
                  <Btn onClick={submitAdd} disabled={!selectedTeam?.id || submitting} className="max-w-full">
                    <span className="min-w-0 truncate">
                      {submitting ? 'Adding...' : selectedTeam ? `Add ${displayTeamName({ ...selectedTeam, teamName: selectedTeam.name || selectedTeam.market }) || selectedTeam.market}` : 'Pick a team'}
                    </span>
                  </Btn>
                </div>
              </Card>
            </section>
          )}
        </div>
      </BmModal>

      {confirmRemove && (
        <ConfirmModal
          title="Remove team"
          body={`Remove ${confirmRemove.name || 'this team'} from ${coachLabel}?`}
          ackLabel="I understand the coach loses this team."
          confirmLabel="Remove team"
          tone="danger"
          busy={removingId === `${confirmRemove.teamId}|${confirmRemove.league || ''}`}
          onConfirm={() => submitRemove(confirmRemove)}
          onClose={() => { if (!removingId) setConfirmRemove(null); }}
        />
      )}

      {/* Rotating an existing code invalidates it for new athletes. */}
      {confirmRotate && (
        <ConfirmModal
          title="Generate new code"
          body={(
            <>
              <p className="text-bm-ink">
                Replace <span className="font-semibold">{coachLabel}</span>'s current code{' '}
                <span className="font-mono font-semibold">{code}</span>?
              </p>
              <p className="mt-2 bm-body-s">
                The old code stops working for new athletes who try to link with it, and every coach of the primary team
                gets the new one. Athletes already linked to this coach stay linked, they're tied to the coach, not the code.
              </p>
            </>
          )}
          ackLabel="I understand the old code stops working."
          confirmLabel="Yes, generate new code"
          busy={generatingCode}
          onConfirm={doGenerateCode}
          onClose={() => { if (!generatingCode) setConfirmRotate(false); }}
        />
      )}

      {/* Nothing is lost here, but the grant opens a whole league's rosters
          to this account, so it keeps the acknowledgment. */}
      {confirmLeague && (
        <ConfirmModal
          title="Make league admin"
          body={(
            <>
              <p className="text-bm-ink">
                Give <span className="font-semibold">{coachLabel}</span> admin access to all of{' '}
                <span className="font-semibold">{confirmLeague.label}</span>?
              </p>
              <p className="mt-2 bm-body-s">
                They'll be able to view every team in the league (all genders and age groups), with rosters and the
                CacheShare tool for each. You can remove this access later.
              </p>
            </>
          )}
          ackLabel="I want this account to see the whole league."
          cancelLabel="Cancel"
          confirmLabel="Yes, make league admin"
          busy={savingLeague}
          onConfirm={handleMakeLeagueAdmin}
          onClose={() => { if (!savingLeague) setConfirmLeague(null); }}
        />
      )}

      {confirmClearLeague && (
        <ConfirmModal
          title="Remove league admin"
          body={`Remove league-admin access from ${coachLabel}?`}
          ackLabel="I understand they lose access to the whole league."
          confirmLabel="Remove league admin"
          tone="danger"
          busy={savingLeague}
          onConfirm={handleClearLeagueAdmin}
          onClose={() => { if (!savingLeague) setConfirmClearLeague(false); }}
        />
      )}
    </>
  );
};

export default CoachTeamsDialog;
